import { useCallback, useEffect, useState } from 'react';
import { loadReportsPersonalDashboard } from '../../application/loadReportsPersonalDashboard';
import type { PersonalDashboardDTO, PersonalOperatorFacts } from '../../domain/reportsPersonalTypes';
import type { ReportsQueryScope } from '../../domain/reportsQueryScope';
import {
  getReportsDateRange,
  formatReportsLoadError,
} from '../reportsScreen.utils';
import type { DateRangePreset } from '../reportsScreen.constants';

type Params = {
  currentBranchId: string | null | undefined;
  userId: string | null | undefined;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  reportsQueryScope: ReportsQueryScope;
  shouldLoadPersonalDashboard: boolean;
};

export function useReportsPersonalPane({
  currentBranchId,
  userId,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  reportsQueryScope,
  shouldLoadPersonalDashboard,
}: Params) {
  const [personalDashboard, setPersonalDashboard] = useState<PersonalDashboardDTO | null>(null);
  const [personalOperatorsForRanking, setPersonalOperatorsForRanking] = useState<PersonalOperatorFacts[]>([]);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [personalLoadError, setPersonalLoadError] = useState<string | null>(null);

  const reloadPersonalDashboard = useCallback(async () => {
    if (!currentBranchId) return;
    const range = getReportsDateRange(
      new Date(),
      dateRangePreset,
      dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null
    );
    setPersonalLoading(true);
    setPersonalLoadError(null);
    try {
      const result = await loadReportsPersonalDashboard({
        branchId: currentBranchId,
        fromIso: range.fromIso,
        toIso: range.toIso,
        scope: reportsQueryScope,
        currentUserId: userId ?? null,
      });
      setPersonalDashboard(result.dashboard);
      setPersonalOperatorsForRanking(result.operatorsForRanking);
    } catch (err) {
      setPersonalLoadError(formatReportsLoadError(err));
    } finally {
      setPersonalLoading(false);
    }
  }, [
    currentBranchId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    reportsQueryScope,
    userId,
  ]);

  useEffect(() => {
    if (!currentBranchId) {
      setPersonalDashboard(null);
      setPersonalOperatorsForRanking([]);
      setPersonalLoading(false);
      setPersonalLoadError(null);
      return;
    }
    if (!shouldLoadPersonalDashboard) return;

    let cancelled = false;
    setPersonalLoadError(null);
    setPersonalLoading(true);

    const range = getReportsDateRange(
      new Date(),
      dateRangePreset,
      dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null
    );

    void (async () => {
      try {
        const result = await loadReportsPersonalDashboard({
          branchId: currentBranchId,
          fromIso: range.fromIso,
          toIso: range.toIso,
          scope: reportsQueryScope,
          currentUserId: userId ?? null,
        });
        if (cancelled) return;
        setPersonalDashboard(result.dashboard);
        setPersonalOperatorsForRanking(result.operatorsForRanking);
      } catch (err) {
        if (!cancelled) {
          setPersonalLoadError(formatReportsLoadError(err));
          setPersonalDashboard(null);
          setPersonalOperatorsForRanking([]);
        }
      } finally {
        if (!cancelled) setPersonalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentBranchId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    reportsQueryScope,
    userId,
    shouldLoadPersonalDashboard,
  ]);

  return {
    personalDashboard,
    personalOperatorsForRanking,
    personalLoading,
    personalLoadError,
    reloadPersonalDashboard,
  };
}
